// Copyright 2026 andrewzeck
// 
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
// 
//     https://www.apache.org/licenses/LICENSE-2.0
// 
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import moment from "moment/moment.js";
import { useState, useEffect, useMemo } from "react";
import CommissionPlanModal from "./CommissionPlanModal.jsx";
import { PrimaryButton } from "@/components/Buttons.jsx";

function CommissionsTable({
  plans,
  loading,
  activeCommissions,
  pastCommissions,
  commissionType,
  onPlanChange
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  let commissions = commissionType === "active-commissions" ? activeCommissions : pastCommissions;
  
  const handleCreateNew = () => {
    setSelectedPlan(null);
    setModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setModalOpen(true);
  };

  const handleModalSave = (savedPlan) => {
    onPlanChange?.();
  };

  return (
    <div>
      {/* Create Plan Button - Only show for plans tab */}
      {commissionType === "plans" && (
        <div className="mb-4 flex justify-end">
          <PrimaryButton onClick={handleCreateNew}>
            Create New Plan
          </PrimaryButton>
        </div>
      )}

     {/* Table */}
     <div className="overflow-x-auto">
     <table className="table-auto w-full dark:text-slate-300">       
       {/* Table body */}
       {loading ? (
        <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
          <tr>
            <td colSpan="6" className="px-2 first:pl-5 last:pr-5 py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Loading Plans...</p>
              </div>
            </td>
          </tr>
        </tbody>
       ) : (
        commissionType === "plans" ? (
          <>
          <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Commission Plan Name</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Commission Type</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Commission Rate</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Product Category</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Status</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Applied Sellers</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left"></div>
              </th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
            {plans.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-2 first:pl-5 last:pr-5 py-8 text-center text-slate-500">
                  No commission plans found. Create one to get started.
                </td>
              </tr>
            ) : (
              plans.map((plan) => {
                return (
                  <tr key={plan._id || plan.id}>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-medium text-slate-800 dark:text-slate-100">
                        {plan.name}
                      </div>
                      {plan.description && (
                        <div className="text-xs text-slate-500 mt-1">{plan.description}</div>
                      )}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left capitalize">{plan.commission_type || plan.type}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">
                        {plan.commission_type === 'tiered' 
                          ? `${plan.tiers?.length || 0} tiers`
                          : plan.commission_type === 'percentage'
                          ? `${(plan.commission_rate * 100).toFixed(0)}%`
                          : `$${plan.commission_rate || plan.rate}`
                        }
                      </div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left capitalize">{plan.product_category}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${
                        plan.active 
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                      }`}>
                        {plan.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {plan.sellers.length}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </>
        ) : 
          <>
          <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Store ID</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Store Name</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Period Start</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Period End</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Commission Type</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Commissions Collected</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Gross Sales Non-Tobacco</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Gross Sales Tobacco</div>
              </th>
            </tr>
          </thead><tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {commissions.map((commission) => {
                return (
                  <tr key={commission._id.toString()}>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{commission.seller_id.store_id}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{commission.seller_id.name}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{moment(commission.period_start).format("MM/DD/YYYY")}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{moment(commission.period_end).format("MM/DD/YYYY")}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{commission.template_id.commission_type}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{commission.commissions_collected_this_period}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{commission.gross_sales_non_tobacco_this_period}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="text-left">{commission.gross_sales_tobacco_this_period}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </>
        )}
     </table>
   </div>

      {/* Commission Plan Modal */}
      <CommissionPlanModal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        plan={selectedPlan}
        onSave={handleModalSave}
      />
 </div>
  )
}

export default CommissionsTable;